import {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {Alert, Card, Form} from 'react-bootstrap';
import Button from '../../../../../Components/Button';
import {
  useGetVirtualPbxNumeros,
  useGetVoiceMailConfig,
  useSaveVoiceMailConfig,
} from '../../../Hooks/TelephonieManager';
import {useAutoDismissMessage} from '../../../Hooks/useAutoDismissMessage';
import {StatutBoiteVocale} from '../TrunkSip/mocks';
import Loading from '../../../../../app/common/components/Loading';

/**
 * MAQUETTE — reproduction de « VoiceMail.aspx » : statut à 3 états, enregistrement
 * d'accueil (upload simulé), langue, email de notification, conservation des messages
 * et code d'accès. Le sélecteur de numéro est affiché pour la fidélité visuelle, mais
 * la configuration renvoyée par le mock est partagée (pas vraiment par numéro).
 */

const STATUT_EXPLICATION_ID: Record<StatutBoiteVocale, string> = {
  actif: 'TELEPHONIE.PBX.BV.STATUT.ACTIF_EXPL',
  annonce_seule: 'TELEPHONIE.PBX.BV.STATUT.ANNONCE_EXPL',
  inactif: 'TELEPHONIE.PBX.BV.STATUT.INACTIF_EXPL',
};

const LANGUES = [
  {value: 'fr', labelId: 'TELEPHONIE.PBX.SONS.TTS.LANGUE.FR'},
  {value: 'de', labelId: 'TELEPHONIE.PBX.SONS.TTS.LANGUE.DE'},
  {value: 'en', labelId: 'TELEPHONIE.PBX.SONS.TTS.LANGUE.EN'},
];

const EMAIL_REGEX = /^[A-Z0-9._%-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

const BoiteVocale = (): JSX.Element | null => {
  const intl = useIntl();
  const {data: numerosData} = useGetVirtualPbxNumeros();
  const {data, isLoading} = useGetVoiceMailConfig();
  const {mutate: sauvegarder} = useSaveVoiceMailConfig();
  const [numeroSelectionne, setNumeroSelectionne] = useState<string | null>(null);
  const [statut, setStatut] = useState<StatutBoiteVocale>('actif');
  const [langue, setLangue] = useState('fr');
  const [email, setEmail] = useState('');
  const [conserverMessages, setConserverMessages] = useState(true);
  const [nouveauMotDePasse, setNouveauMotDePasse] = useState('');
  const [confirmationMotDePasse, setConfirmationMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useAutoDismissMessage();

  useEffect(() => {
    if (data) {
      setStatut(data.statut);
      setLangue(data.langue);
      setEmail(data.email);
      setConserverMessages(data.conserverMessages);
    }
  }, [data]);

  useEffect(() => {
    if (numerosData && numerosData.numerosGlobaux.length > 0 && numeroSelectionne == null) {
      setNumeroSelectionne(numerosData.numerosGlobaux[0].numero);
    }
  }, [numerosData, numeroSelectionne]);

  if (isLoading || !data || !numerosData) {
    return <Loading className="col-md-6" />;
  }

  const decocherConserver = (coche: boolean): void => {
    if (!coche) {
      const confirme = window.confirm(
        intl.formatMessage({id: 'TELEPHONIE.PBX.BV.CONFIRMER_SUPPRESSION_AUTO'})
      );
      if (!confirme) {
        return;
      }
    }
    setConserverMessages(coche);
  };

  const enregistrer = (): void => {
    if (email.trim() !== '' && !EMAIL_REGEX.test(email.trim())) {
      setErreur(intl.formatMessage({id: 'TELEPHONIE.PBX.BV.ERREUR_EMAIL'}));
      return;
    }
    if (!conserverMessages && email.trim() === '') {
      setErreur(intl.formatMessage({id: 'TELEPHONIE.PBX.BV.ERREUR_EMAIL_OBLIGATOIRE'}));
      return;
    }
    if (nouveauMotDePasse !== '' || confirmationMotDePasse !== '') {
      if (!/^\d{4}$/.test(nouveauMotDePasse)) {
        setErreur(intl.formatMessage({id: 'TELEPHONIE.PBX.BV.ERREUR_MOT_DE_PASSE_FORMAT'}));
        return;
      }
      if (nouveauMotDePasse !== confirmationMotDePasse) {
        setErreur(intl.formatMessage({id: 'TELEPHONIE.PBX.BV.ERREUR_MOT_DE_PASSE_CONFIRMATION'}));
        return;
      }
    }
    setErreur(null);
    sauvegarder({
      statut,
      nomEnregistrement: data.nomEnregistrement,
      langue,
      email: email.trim(),
      conserverMessages,
      codeAcces: data.codeAcces,
    });
    setNouveauMotDePasse('');
    setConfirmationMotDePasse('');
    setMessage(intl.formatMessage({id: 'TELEPHONIE.SVI.SUCCESS_MESSAGE'}));
  };

  return (
    <div>
      <Card bg="light" className="mb-5">
        <Card.Body>
          <div className="fw-bold text-uppercase fs-6 mb-1">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.BV.TITLE'})}
          </div>
          <p className="text-muted fs-7 mb-0">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.BV.DESCRIPTION'})}
          </p>
        </Card.Body>
      </Card>

      {message && <div className="fw-bold text-danger mb-3">{message}</div>}
      {erreur && (
        <Alert variant="danger" onClose={() => setErreur(null)} dismissible>
          {erreur}
        </Alert>
      )}

      <Card className="mb-5">
        <Card.Body>
          <Form.Label className="fw-bold fs-6">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.BV.NUMERO'})}
          </Form.Label>
          <Form.Select
            style={{maxWidth: 320}}
            value={numeroSelectionne ?? ''}
            onChange={(e) => setNumeroSelectionne(e.target.value)}
          >
            {numerosData.numerosGlobaux.map((numero) => (
              <option key={numero.id} value={numero.numero}>
                {numero.numeroFormate} ({numero.nom})
              </option>
            ))}
          </Form.Select>

          <div className="fw-bold fs-6 mt-4 mb-2">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.BV.STATUT_LABEL'})}
          </div>
          <div className="d-flex gap-4 mb-3">
            <Form.Check
              type="radio"
              id="statut-actif"
              label={intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.APPELS_REPONDUS.ACTIF'})}
              checked={statut === 'actif'}
              onChange={() => setStatut('actif')}
            />
            <Form.Check
              type="radio"
              id="statut-annonce"
              label={intl.formatMessage({id: 'TELEPHONIE.PBX.BV.STATUT.ANNONCE_SEULE'})}
              checked={statut === 'annonce_seule'}
              onChange={() => setStatut('annonce_seule')}
            />
            <Form.Check
              type="radio"
              id="statut-inactif"
              label={intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.APPELS_REPONDUS.INACTIF'})}
              checked={statut === 'inactif'}
              onChange={() => setStatut('inactif')}
            />
          </div>
          <div className="bg-primary text-white rounded p-3 mb-4 fs-7">
            {intl.formatMessage({id: STATUT_EXPLICATION_ID[statut]})}
          </div>

          <div className="fw-bold fs-6 mb-2">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.BV.ENREGISTREMENT_LABEL'})}
          </div>
          <div className="d-flex align-items-center flex-wrap gap-3 mb-4">
            <span className="fs-6">
              {data.nomEnregistrement ?? intl.formatMessage({id: 'TELEPHONIE.PBX.BV.AUCUN_FICHIER'})}
            </span>
            <Form.Control type="file" accept=".mp3,.wav" style={{maxWidth: 260}} />
          </div>

          <Form.Label className="fw-bold fs-6">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.BV.LANGUE_LABEL'})}
          </Form.Label>
          <Form.Select
            className="mb-4"
            style={{maxWidth: 200}}
            value={langue}
            onChange={(e) => setLangue(e.target.value)}
          >
            {LANGUES.map((option) => (
              <option key={option.value} value={option.value}>
                {intl.formatMessage({id: option.labelId})}
              </option>
            ))}
          </Form.Select>

          <Form.Label className="fw-bold fs-6">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.BV.EMAIL_LABEL'})}
          </Form.Label>
          <Form.Control
            className="mb-4"
            style={{maxWidth: 320}}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <Form.Check
            className="mb-4"
            type="checkbox"
            id="conserver-messages"
            label={intl.formatMessage({id: 'TELEPHONIE.PBX.BV.CONSERVER_LABEL'})}
            checked={conserverMessages}
            onChange={(e) => decocherConserver(e.target.checked)}
          />

          <div className="fw-bold fs-6 mb-2">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.BV.CODE_ACCES_LABEL'})} : {data.codeAcces}
          </div>
          <div className="d-flex flex-wrap gap-4">
            <div>
              <Form.Label className="fs-6">
                {intl.formatMessage({id: 'TELEPHONIE.PBX.BV.NOUVEAU_MOT_DE_PASSE'})}
              </Form.Label>
              <Form.Control
                type="password"
                maxLength={4}
                style={{maxWidth: 140}}
                value={nouveauMotDePasse}
                onChange={(e) => setNouveauMotDePasse(e.target.value.replace(/\D/g, ''))}
              />
            </div>
            <div>
              <Form.Label className="fs-6">
                {intl.formatMessage({id: 'TELEPHONIE.PBX.BV.CONFIRMATION'})}
              </Form.Label>
              <Form.Control
                type="password"
                maxLength={4}
                style={{maxWidth: 140}}
                value={confirmationMotDePasse}
                onChange={(e) => setConfirmationMotDePasse(e.target.value.replace(/\D/g, ''))}
              />
            </div>
          </div>
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-center">
        <Button
          label={intl.formatMessage({id: 'TELEPHONIE.SVI.MENU.FORM.SAVE'})}
          onClick={enregistrer}
        />
      </div>
    </div>
  );
};

export default BoiteVocale;
